import type {
  CreateConselhoSuperiorDto,
  ReadConselhoSuperiorDto,
} from '@/api.types';
import type { ConselhoSuperiorFormDto } from './conselho-superior.schema';

export function resolverBienio(ano: number): { anoInicio: number; anoFim: number } {
  const anoInicio = ano % 2 === 0 ? ano : ano - 1;
  return { anoInicio, anoFim: anoInicio + 2 };
}

export function bienioAtual(agora = new Date()): { anoInicio: number; anoFim: number } {
  return resolverBienio(agora.getFullYear());
}

export function rotuloBienio(anoInicio: number, anoFim: number): string {
  return `${anoInicio}/${anoFim}`;
}

/** Linha de impressão no estilo: "DEFENSOR PÚBLICO, DR. NOME COMPLETO" */
export function formatarLinhaImpressaoMembro(opts: {
  nome?: string | null;
  titulo?: string | null;
}): string {
  const nome = String(opts.nome ?? '').trim();
  if (!nome) return '';
  const cargo = String(opts.titulo ?? 'Defensor Público').trim() || 'Defensor Público';
  return `${cargo.toUpperCase()} ${nome.toUpperCase()}`;
}

function escapeHtmlTexto(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function linhaMembroHtml(linha: string): string {
  const idx = linha.indexOf(',');
  if (idx < 0) {
    return `<p class="cs-membro">${escapeHtmlTexto(linha)}</p>`;
  }
  const cargo = `${linha.slice(0, idx + 1)} `;
  const nome = linha.slice(idx + 1).trimStart();
  return `<p class="cs-membro"><strong>${escapeHtmlTexto(cargo)}</strong>${escapeHtmlTexto(nome)}</p>`;
}

/** Membro nato: cargo numa linha, nome na seguinte. */
function linhaNatoHtml(cargo: string, nome?: string | null): string {
  const cargoFmt = cargo.replace(/:$/, '').trim().toUpperCase();
  const nomeFmt = String(nome ?? '').trim().toUpperCase() || '—';
  return `
    <div class="cs-nato">
      <p class="cs-membro cs-nato-cargo"><strong>${escapeHtmlTexto(cargoFmt)}</strong></p>
      <p class="cs-membro cs-nato-nome">${escapeHtmlTexto(nomeFmt)}</p>
    </div>
  `;
}

function secaoHtml(titulo: string, linhas: string[]): string {
  const corpo =
    linhas.length === 0
      ? '<p class="cs-vazio">—</p>'
      : linhas.map(linhaMembroHtml).join('');
  return `
    <section class="cs-secao">
      <h2 class="cs-titulo">${escapeHtmlTexto(titulo)}</h2>
      ${corpo}
    </section>
  `;
}

function secaoNatosHtml(dados: {
  defensorPublicoGeral?: { nome?: string } | null;
  subdefensorInstitucionais?: { nome?: string } | null;
  subdefensorAdministrativos?: { nome?: string } | null;
  corregedorOuvidorGeral?: { nome?: string } | null;
}): string {
  return `
    <section class="cs-secao">
      <h2 class="cs-titulo cs-titulo-natos">MEMBROS NATOS</h2>
      ${linhaNatoHtml('DEFENSOR PÚBLICO-GERAL', dados.defensorPublicoGeral?.nome)}
      ${linhaNatoHtml(
    'SUBDEFENSOR PÚBLICO-GERAL PARA ASSUNTOS INSTITUCIONAIS',
    dados.subdefensorInstitucionais?.nome,
  )}
      ${linhaNatoHtml(
    'SUBDEFENSOR PÚBLICO-GERAL PARA ASSUNTOS ADMINISTRATIVOS',
    dados.subdefensorAdministrativos?.nome,
  )}
      ${linhaNatoHtml(
    'CORREGEDOR-GERAL/OUVIDOR-GERAL',
    dados.corregedorOuvidorGeral?.nome,
  )}
    </section>
  `;
}

/** HTML da impressão institucional do Conselho Superior (layout centralizado em verde). */
export function montarHtmlImpressaoConselho(dados: {
  anoInicio: number;
  anoFim: number;
  defensorPublicoGeral?: { nome?: string } | null;
  subdefensorInstitucionais?: { nome?: string } | null;
  subdefensorAdministrativos?: { nome?: string } | null;
  corregedorOuvidorGeral?: { nome?: string } | null;
  membros?: Array<{
    tipo: string;
    defensor?: { id?: number; nome?: string; titulo?: string | null } | null;
    pessoa?: { id?: string; nome?: string } | null;
  }>;
}): string {
  const bienio = rotuloBienio(dados.anoInicio, dados.anoFim);
  const membros = dados.membros ?? [];

  const linhasTipo = (tipo: string, usarPessoa: boolean) =>
    membros
      .filter((m) => m.tipo === tipo)
      .map((m) =>
        formatarLinhaImpressaoMembro(
          usarPessoa
            ? { nome: m.pessoa?.nome, titulo: 'Defensor Público' }
            : { nome: m.defensor?.nome, titulo: m.defensor?.titulo },
        ),
      )
      .filter(Boolean);

  return `
    <style>
      @page { size: A4; margin: 18mm; }
      body {
        font-family: Arial, Helvetica, sans-serif;
        color: #1b5e3b;
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .cs-impressao { text-align: center; color: #1b5e3b; }
      .cs-secao { margin: 0 0 2.5rem; }
      .cs-titulo {
        font-size: 14pt;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        margin: 0 0 1.1rem;
        color: #1b5e3b;
      }
      .cs-titulo-natos { font-size: 16pt; margin-bottom: 1.35rem; }
      .cs-membro {
        font-size: 12pt;
        line-height: 1.45;
        text-transform: uppercase;
        margin: 0 0 0.75rem;
        color: #1b5e3b;
      }
      .cs-membro strong { font-weight: 700; }
      .cs-nato { margin: 0 0 1rem; }
      .cs-nato-cargo { margin-bottom: 0.15rem; }
      .cs-nato-nome { margin-bottom: 0; font-weight: 400; }
      .cs-vazio { font-style: italic; opacity: 0.7; }
    </style>
    <div class="cs-impressao">
      ${secaoNatosHtml(dados)}
      ${secaoHtml(`MEMBROS ELEITOS DE 1ª CATEGORIA – BIÊNIO ${bienio}:`, linhasTipo('PRIMEIRA_CATEGORIA', false))}
      ${secaoHtml(`MEMBROS ELEITOS DE 2ª CATEGORIA – BIÊNIO ${bienio}:`, linhasTipo('SEGUNDA_CATEGORIA', false))}
      ${secaoHtml('PARTICIPANTE COM DIREITO A VOZ:', linhasTipo('PARTICIPANTE_VOZ', true))}
    </div>
  `;
}



export function dtoToForm(dto: ReadConselhoSuperiorDto): ConselhoSuperiorFormDto {
  const membros = dto.membros ?? [];
  return {
    id: dto.id ?? null,
    anoInicio: dto.anoInicio,
    anoFim: dto.anoFim,
    defensorPublicoGeralId: dto.defensorPublicoGeral?.id ?? null,
    subdefensorInstitucionaisId: dto.subdefensorInstitucionais?.id ?? null,
    subdefensorAdministrativosId: dto.subdefensorAdministrativos?.id ?? null,
    corregedorOuvidorGeralId: dto.corregedorOuvidorGeral?.id ?? null,
    primeiraCategoriaIds: membros
      .filter((m) => m.tipo === 'PRIMEIRA_CATEGORIA' && m.defensor?.id)
      .map((m) => m.defensor!.id),
    segundaCategoriaIds: membros
      .filter((m) => m.tipo === 'SEGUNDA_CATEGORIA' && m.defensor?.id)
      .map((m) => m.defensor!.id),
    participantesVoz: membros
      .filter((m) => m.tipo === 'PARTICIPANTE_VOZ' && m.pessoa?.id)
      .map((m) => ({ id: m.pessoa!.id, nome: m.pessoa!.nome ?? m.pessoa!.id })),
  };
}

export function formToPayload(form: ConselhoSuperiorFormDto): CreateConselhoSuperiorDto {
  return {
    anoInicio: form.anoInicio,
    anoFim: form.anoFim,
    defensorPublicoGeralId: form.defensorPublicoGeralId ?? null,
    subdefensorInstitucionaisId: form.subdefensorInstitucionaisId ?? null,
    subdefensorAdministrativosId: form.subdefensorAdministrativosId ?? null,
    corregedorOuvidorGeralId: form.corregedorOuvidorGeralId ?? null,
    membros: [
      ...form.primeiraCategoriaIds.map((defensorId, ordem) => ({
        tipo: 'PRIMEIRA_CATEGORIA' as const,
        defensorId,
        ordem,
      })),
      ...form.segundaCategoriaIds.map((defensorId, ordem) => ({
        tipo: 'SEGUNDA_CATEGORIA' as const,
        defensorId,
        ordem,
      })),
      ...form.participantesVoz.map((p, ordem) => ({
        tipo: 'PARTICIPANTE_VOZ' as const,
        pessoaId: p.id,
        ordem,
      })),
    ],
  };
}
